<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->
<crucible-dialog
  dialogTitle="Edit Task"
  submitLabel="Save"
  [submitDisabled]="!errorFree()"
  [guardUnsavedWork]="true"
  (cancel)="handleEditComplete(false)"
  (submit)="handleEditComplete(true)"
>
<div crucibleDialogContent>
  <div class="content">
    <mat-form-field class="param-full-width">
      <mat-label>Name</mat-label>
      <input
        matInput
        [value]="data.task.name"
        (input)="data.task.name = $event.target.value"
        cdkFocusInitial
        />
    </mat-form-field>
    <mat-form-field class="param-full-width">
      <mat-label>Description</mat-label>
      <input
        matInput
        [value]="data.task.description"
        (input)="data.task.description = $event.target.value"
        />
    </mat-form-field>

    <h2>Action</h2>
    <mat-form-field class="param-full-width">
      <mat-label>Select an Action</mat-label>
      <mat-select
        [(value)]="selectedCommand"
        (selectionChange)="onCommandChange()"
        >
        @for (cmd of availableCommands; track cmd) {
          <mat-option [value]="cmd">
            {{ cmd.api }} - {{ cmd.display }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
    @if (selectedCommand != undefined) {
      <span>
        @for (param of selectedCommand.parameters; track param) {
          <span
            class="param-indent"
            >
            @if (
              (param.inputType === 'text' || param.inputType === 'textArea') &&
              !(param.key.toLowerCase() === 'username' && param.requiresVm)
              ) {
              <mat-form-field
                class="param-full-width"
                >
                <mat-label>{{ param.display }}</mat-label>
                @if (param.inputType === 'text') {
                  <input
                    matInput
                    placeholder="{{ param.display }}"
                    [(ngModel)]="param.value"
                    [value]="param.value"
                    (input)="param.value = $event.target.value"
                    (change)="onCommandChange()"
                    [matTooltip]="param.hint"
                    />
                }
                @if (param.inputType === 'textArea') {
                  <textarea
                    matInput
                    class="mdc-text-field__input"
                    rows="10"
                    cols="160"
                    placeholder="{{ param.display }}"
                    [value]="param.value"
                    (input)="param.value = $event.target.value"
                    (change)="onCommandChange()"
                    [matTooltip]="param.hint"
                  ></textarea>
                }
              </mat-form-field>
            }
            @if (param.inputType === 'select') {
              <mat-form-field
                class="param-full-width"
                >
                <mat-label>{{ param.display }}</mat-label>
                <mat-select
                  [(ngModel)]="param.value"
                  (selectionChange)="onCommandChange()"
                  name="{{ param.key }}"
                  [matTooltip]="param.hint"
                  >
                  @for (choice of param.choices; track choice) {
                    <mat-option
                      [value]="choice.key"
                      >
                      {{ choice.display }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (param.inputType === 'checkbox') {
              <mat-checkbox
                class="param-full-width"
                name="{{ param.key }}"
                [matTooltip]="param.hint"
                [(ngModel)]="param.value"
                (change)="onCommandChange()"
                >
                {{ param.display }}
              </mat-checkbox>
            }
            @if (param.inputType === 'file') {
              <span class="param-full-width">
                <input
                  #file
                  id="fileInput"
                  type="file"
                  multiple
                  style="display: none"
                  (change)="loadFileContent(param, file)"
                  [matTooltip]="param.hint"
                  />
                <button mat-menu-item (click)="file.click()">
                  Select a Text File (... or enter text below)
                </button>
                <textarea
                  class="mdc-text-field__input"
                  rows="10"
                  cols="160"
                  placeholder="{{ param.display }}"
                  [(ngModel)]="param.value"
                  name="content"
                  (change)="onCommandChange()"
                ></textarea>
              </span>
            }
          </span>
        }
      </span>
    }
    <mat-form-field class="param-full-width">
      <mat-label>Trigger Condition</mat-label>
      <mat-select
        [(ngModel)]="data.task.triggerCondition"
        name="triggerCondition"
        >
        @for (choice of triggerConditions; track choice) {
          <mat-option [value]="choice">
            {{ choice }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
    <mat-form-field class="param-full-width">
      <mat-label>Expected Output</mat-label>
      <input
        matInput
        [value]="data.task.expectedOutput"
        (input)="data.task.expectedOutput = $event.target.value"
        />
    </mat-form-field>

    <h2>Scoring</h2>
    <mat-form-field class="param-full-width">
      <mat-label>Score</mat-label>
      <input
        matInput
        type="number"
        [(ngModel)]="data.task.score"
        />
    </mat-form-field>
    <mat-checkbox
      [(ngModel)]="data.task.userExecutable"
      matTooltip="Can an unprivileged member of the Task's Scenario execute this Task?"
      >User Executable</mat-checkbox
      >
      <mat-checkbox
        [(ngModel)]="data.task.repeatable"
        matTooltip="Can this Task be executed again once successful?"
        >Repeatable</mat-checkbox
        >

        <h2>Delay / Iteration / Expiration</h2>
        <mat-form-field class="param-full-width">
          <mat-label>Delay (seconds)</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="data.task.delaySeconds"
            />
        </mat-form-field>
        <mat-form-field class="param-full-width">
          <mat-label>Number of Iterations</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="data.task.iterations"
            />
        </mat-form-field>
        <mat-form-field class="param-full-width">
          <mat-label>Interval (seconds)</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="data.task.intervalSeconds"
            />
        </mat-form-field>
        <mat-form-field class="param-full-width">
          <mat-label>IterationTermination</mat-label>
          <mat-select
            [(ngModel)]="data.task.iterationTermination"
            name="iterationTermination"
            >
            @for (choice of iterationTerminations; track choice) {
              <mat-option
                [value]="choice"
                >
                {{ choice }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field class="param-full-width">
          <mat-label>Expiration Timeout (seconds)</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="data.task.expirationSeconds"
            />
        </mat-form-field>

        @if (selectedCommand && selectedCommand.requiresVm) {
          <span>
            @if (credentialsAreRequired()) {
              <span>
                <h2>VM Credentials</h2>
                <app-vm-credentials
                  [scenarioTemplateId]="data.task.scenarioTemplateId"
                  [scenarioId]="data.task.scenarioId"
                  [username]="username | async"
                  [password]="password | async"
                  (selectedVmCredentialChanged)="handleVmCredentialChange($event)"
                ></app-vm-credentials>
              </span>
            }
            <h2>VM Selection</h2>
            @if (!chooseVms) {
              <mat-form-field class="param-full-width">
                <mat-label>VM Mask (VM name includes this text)</mat-label>
                <input
                  matInput
                  [value]="data.task.vmMask"
                  (input)="data.task.vmMask = $event.target.value"
                  />
              </mat-form-field>
            }
            @if (!data.task.scenarioTemplateId) {
              <mat-checkbox
                [checked]="chooseVms"
                (change)="switchChooseVmsMethod($event)"
                style="margin-right: 20px"
                >Choose Actual VMs</mat-checkbox
                >
              }
              @if (chooseVms) {
                <app-vm-list
                  [selectedVms]="data.task.vmList || []"
                  (updateVmList)="handleUpdateVmList($event)"
                  >
                </app-vm-list>
              }
            </span>
          }
        </div>
      </div>
</crucible-dialog>
